import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/config";
import { useCompare } from "@/lib/compare";
import { Check, X, ArrowRight, Clock, Users, Mountain, MapPin, GitCompareArrows } from "lucide-react";

export default function ComparePage() {
  const { slugs, remove, clear } = useCompare();
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (slugs.length === 0) { setTours([]); setLoading(false); return; }
    setLoading(true);
    Promise.all(slugs.map((s) => api.get(`/tours/${s}`).then((r) => r.data).catch(() => null)))
      .then((list) => setTours(list.filter(Boolean)))
      .finally(() => setLoading(false));
  }, [slugs.join(",")]); // eslint-disable-line

  if (loading) return <div className="pt-40 text-center text-[#23170E]/55">Loading comparison…</div>;

  if (tours.length === 0) {
    return (
      <div data-testid="compare-empty" className="pt-36 md:pt-44 pb-28 px-6 min-h-[70vh] bg-[#FAF6F0]">
        <div className="max-w-xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#A6521B]/12 text-[#A6521B] mb-7">
            <GitCompareArrows size={24} />
          </div>
          <p className="overline">side by side</p>
          <h1 className="font-display text-5xl md:text-6xl italic font-light mt-4 leading-tight">Nothing Pinned Yet</h1>
          <p className="mt-6 text-[#23170E]/65 leading-relaxed">
            Tap the pin on any two safaris and we'll lay them out side by side — days, difficulty, parks,
            what's included and what isn't — so the decision makes itself.
          </p>
          <Link to="/tours" data-testid="compare-browse-cta" className="inline-flex mt-9 btn-pill btn-pill-primary">
            Browse Safaris <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    );
  }

  const allIncluded = [...new Set(tours.flatMap((t) => t.inclusions))].sort();
  const identical = tours.length > 1 && allIncluded.every((item) => tours.every((t) => t.inclusions.includes(item)));

  return (
    <div data-testid="compare-page" className="pt-36 md:pt-44 pb-28 px-6 md:px-12 bg-[#FAF6F0]">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="overline">side by side</p>
            <h1 className="font-display text-5xl md:text-6xl italic font-light mt-4 leading-tight">
              Which Trip Wins?
            </h1>
          </div>
          <button data-testid="compare-clear-all" onClick={clear} className="text-sm text-[#23170E]/60 hover:text-[#A6521B]">
            Clear comparison
          </button>
        </div>

        {tours.length === 1 && (
          <p data-testid="compare-need-one-more" className="mt-8 rounded-3xl border border-[#A6521B]/35 bg-[#A6521B]/8 p-5 text-sm">
            Pin one more safari and this becomes a true side-by-side. <Link to="/tours" className="text-[#A6521B] underline">Browse safaris</Link>
          </p>
        )}

        {/* Headers */}
        <div className={`mt-12 grid gap-6 ${tours.length > 1 ? "md:grid-cols-2" : "md:grid-cols-1 max-w-lg"}`}>
          {tours.map((t) => (
            <div key={t.slug} data-testid={`compare-col-${t.slug}`} className="bg-[#FFFFFF] border border-[#E2D5C3] rounded-3xl shadow-[0_4px_20px_-2px_rgba(35,23,14,0.06)] overflow-hidden">
              <div className="relative aspect-[16/10]">
                <img src={t.hero_image} alt={t.title} className="w-full h-full object-cover" />
                <button
                  data-testid={`compare-col-remove-${t.slug}`}
                  onClick={() => remove(t.slug)}
                  className="absolute top-3 right-3 w-9 h-9 rounded-full bg-[#FAF6F0]/90 text-[#23170E] hover:bg-[#23170E] hover:text-[#FAF6F0] flex items-center justify-center transition-colors"
                  aria-label={`Remove ${t.title}`}
                >
                  <X size={15} />
                </button>
              </div>
              <div className="p-6">
                <h2 className="font-marcellus text-2xl leading-tight">{t.title}</h2>
                {t.subtitle && <p className="text-sm text-[#23170E]/60 mt-1.5">{t.subtitle}</p>}
                <Link to={`/booking?tour=${t.slug}`} data-testid={`compare-book-${t.slug}`} className="mt-5 w-full btn-pill btn-pill-primary justify-center text-sm">
                  Book This One
                </Link>
                <Link to={`/tours/${t.slug}`} className="mt-2.5 w-full btn-pill btn-pill-outline justify-center text-sm">
                  Full itinerary
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* At-a-glance rows */}
        <div className="mt-14 rounded-3xl border border-[#E2D5C3] overflow-hidden bg-[#FFFFFF]">
          <Row icon={Clock} label="Duration" tours={tours} render={(t) => t.duration_label} />
          <Row icon={Mountain} label="Difficulty" tours={tours} render={(t) => t.difficulty} />
          <Row icon={Users} label="Group size" tours={tours} render={(t) => `${t.group_size} guests`} />
          <Row icon={MapPin} label="Parks & regions" tours={tours} render={(t) => t.park} />
          <Row label="Days on itinerary" tours={tours} render={(t) => `${t.itinerary.length} days`} />
          <Row
            label="Highlights"
            tours={tours}
            render={(t) => (
              <ul className="space-y-1.5">
                {t.highlights.slice(0, 5).map((h) => (
                  <li key={h} className="flex items-start gap-2">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#A6521B] flex-shrink-0" />
                    <span className="leading-snug">{h}</span>
                  </li>
                ))}
              </ul>
            )}
          />
        </div>

        {/* What's included — differences made obvious */}
        {tours.length > 1 && (
          <div className="mt-14">
            <h2 className="font-display text-3xl md:text-4xl italic font-light">What's included</h2>
            <p className="mt-3 text-sm text-[#23170E]/60">
              {identical
                ? "These two trips include exactly the same things — so the choice comes down to days, parks and pace above."
                : "A tick means that trip covers it. Highlighted rows are where they differ."}
            </p>
            <div className="mt-7 rounded-3xl border border-[#E2D5C3] overflow-hidden bg-[#FFFFFF]">
              {allIncluded.map((item, i) => {
                const flags = tours.map((t) => t.inclusions.includes(item));
                const differs = flags.some((f) => f !== flags[0]);
                return (
                  <div
                    key={item}
                    data-testid={`compare-inclusion-${i}`}
                    className={`grid grid-cols-[1fr_auto] md:grid-cols-[1fr_120px_120px] gap-3 px-5 md:px-7 py-4 items-center border-b border-[#E2D5C3] last:border-0 ${
                      differs ? "bg-[#A6521B]/6" : ""
                    }`}
                  >
                    <span className="text-sm leading-snug">{item}</span>
                    {flags.map((ok, idx) => (
                      <span key={idx} className="justify-self-center">
                        {ok ? <Check size={17} className="text-[#2E6038]" /> : <X size={17} className="text-[#A6352B]/55" />}
                      </span>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-16 text-center">
          <p className="font-display text-3xl md:text-4xl italic font-light">Still torn between them?</p>
          <p className="mt-4 text-[#23170E]/65 max-w-xl mx-auto leading-relaxed">
            Tell us your dates and we'll say honestly which one fits your group better — no upselling.
          </p>
          <Link to="/contact" data-testid="compare-ask-cta" className="inline-flex mt-8 btn-pill btn-pill-primary">
            Ask a Guide <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, tours, render }) {
  return (
    <div className={`grid gap-4 px-5 md:px-7 py-5 border-b border-[#E2D5C3] last:border-0 ${tours.length > 1 ? "md:grid-cols-[180px_1fr_1fr]" : "md:grid-cols-[180px_1fr]"}`}>
      <p className="text-xs uppercase tracking-[0.18em] text-[#23170E]/50 flex items-center gap-2">
        {Icon && <Icon size={13} className="text-[#A6521B]" />} {label}
      </p>
      {tours.map((t) => (
        <div key={t.slug} className="text-sm text-[#23170E]">{render(t)}</div>
      ))}
    </div>
  );
}
